import * as React from 'react';
import _ from 'lodash';

import {
  iconName,
  qualify,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  withMeta,
  withSystem,
  Plugins,
  type AtomConfig,
  Atom,
} from '@packages/systems/wfdl';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import {getTagFromData as getElementTag} from '@packages/utilities/platform';
import {
  isFigureMediaWrapperElement,
  isTypographicElement,
} from '@packages/utilities/elementTypeUtils';
import {
  Expression,
  TListOfElement,
  TTypeConstructor,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {getChildren} from '@packages/utilities/data-utils';

type FlexDirection = 'column' | 'row';

export const basicPlugin = Plugins.name('Basic');

export const metaByTag: {
  [key: string]: {
    displayName: string;
    icon?: any;
  };
} = {
  div: {
    displayName: 'Flex Block',
  },
  footer: {
    displayName: 'Footer',
  },
  header: {
    displayName: 'Header',
  },
  main: {
    displayName: 'Main',
  },
  nav: {
    displayName: 'Navigation',
    icon: qualify(Plugins.name('Navbar'), iconName('Nav')),
  },
  aside: {
    displayName: 'Aside',
  },
  address: {
    displayName: 'Address',
  },
  article: {
    displayName: 'Article',
  },
  section: {
    displayName: 'Section',
  },
};

const Flex = Atom.createRender(
  ({attributes, classes, generateAttributes, data, renderChild}) => {
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'.
    const Tag = data.tag ?? 'div';

    return (
      <Tag {...generateAttributes(attributes, classes)}>
        {getChildren(data).map((child) => renderChild(child))}
      </Tag>
    );
  }
);

export const createFlexAtom = (flexDirection: FlexDirection): AtomConfig => {
  return createDevlinkAtom({
    displayName: {
      type: 'Derived',
      defaultValue: 'Flex Block',
      getFromElement(
        element: ExpressionElement,
        parent: null | undefined | Expression
      ) {
        const tag = getElementTag(element);
        const byTag = metaByTag[tag];

        if (tag === 'div' && isTypographicElement(element)) {
          return 'Text Block';
        }

        if (parent && isFigureMediaWrapperElement(element, parent)) {
          return 'Media';
        }

        if (byTag && byTag.displayName) {
          return byTag.displayName;
        }

        return _.startCase(tag);
      },
    },
    style: {
      base: {
        main: {
          noPseudo: {
            alignItems: 'flex-start',
            display: 'flex',
            flexDirection,
          },
        },
      },
    },
    icon: {
      type: 'Derived',
      getFromElement: (element, parent) => {
        const tag = getElementTag(element);
        const byTag = metaByTag[tag];

        if (tag === 'div' && isTypographicElement(element)) {
          return [basicPlugin, iconName('Text')];
        }

        if (parent && isFigureMediaWrapperElement(element, parent)) {
          return [basicPlugin, iconName('FigureMedia')];
        }

        if (byTag && byTag.icon) {
          // Eventually:
          // return [basicPlugin, iconName(icon)];
          return byTag.icon;
        }

        return [basicPlugin, iconName('Div')];
      },
    },
    showEmpty: true,
    render: Flex,
    fields: {
      children: TListOfElement,
      tag: withMeta(
        {variant: 'menu'},
        withSystem(
          {
            defaultValue: 'div',
            label: 'Tag',
            private: true,
            isLocalizable: true,
          },
          TTypeConstructor(
            qualify(pluginName('Basic'), typeName('TagsDropdown'))
          )
        )
      ),
    },
  });
};
